import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Users, Lock, Sparkles, PieChart, TrendingUp, Palette } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import RealEstateMarketGrowth from '@/components/RealEstateMarketGrowth';
import FractionalMarketGrowth from '@/components/FractionalMarketGrowth';
import MembershipBenefits from '@/components/MembershipBenefits';
import DigitalGlobe from '@/components/DigitalGlobe';
import FAQSection from '@/components/FAQSection';
import WelcomeModal from '@/components/WelcomeModal';
import RealEstateCalculatorCard from '@/components/RealEstateCalculatorCard';
import HeroCalculator from '@/components/HeroCalculator';

export default function Landing() {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className="relative bg-black">
      {/* Welcome Modal for First-Time Visitors */}
      <WelcomeModal />
      
      {/* Continuous 3D Background Effects */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Animated Grid Lines */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(to right, #4ADE80 1px, transparent 1px),
              linear-gradient(to bottom, #4ADE80 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px',
            transform: 'perspective(1000px) rotateX(60deg)',
            transformOrigin: 'center top'
          }}
        />
        
        {/* Floating Geometric Shapes */}
        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="shapeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4ADE80" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#4ADE80" stopOpacity="0" />
            </linearGradient>
          </defs>
          
          {/* Animated circles */}
          <circle cx="10%" cy="20%" r="150" fill="url(#shapeGradient)" className="animate-float" />
          <circle cx="85%" cy="40%" r="200" fill="url(#shapeGradient)" className="animate-float-delayed" />
          <circle cx="15%" cy="70%" r="180" fill="url(#shapeGradient)" className="animate-float-slow" />
          <circle cx="90%" cy="85%" r="160" fill="url(#shapeGradient)" className="animate-float" />
        </svg>
        
        {/* Gradient Orbs */}
        <div className="absolute top-[20%] left-[10%] w-[500px] h-[500px] bg-[#4ADE80]/5 rounded-full filter blur-[120px] animate-pulse" />
        <div className="absolute top-[60%] right-[15%] w-[600px] h-[600px] bg-[#4ADE80]/5 rounded-full filter blur-[150px] animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-[10%] left-[40%] w-[400px] h-[400px] bg-[#4ADE80]/3 rounded-full filter blur-[100px] animate-pulse" style={{ animationDelay: '4s' }} />
      </div>
      
      {/* Hero Section - Left Aligned */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-transparent">
        {/* Animated Background - Local to hero */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#4ADE80]/8 rounded-full filter blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#4ADE80]/5 rounded-full filter blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left Side - Content */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="flex flex-col justify-center"
            >
              {/* Main Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 leading-tight text-white"
              >
                Own premium real estate—
                <span className="block mt-3 font-normal text-[#4ADE80]">
                  fractionally, transparently,
                </span>
                <span className="block mt-3 font-normal text-[#4ADE80]">
                  and intelligently.
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg text-white/70 mb-10 leading-relaxed max-w-xl"
              >
                Structured ownership of high-quality residential assets, starting at a fraction of traditional entry points.
              </motion.p>

              {/* App Store Badges - Smaller */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row items-start gap-3"
              >
                <a href="#" className="block">
                  <img 
                    src="/app-store-badge.png"
                    alt="Download on App Store"
                    className="h-11 hover:opacity-80 transition-opacity"
                    onError={(e) => {
                      e.target.outerHTML = '<div class="bg-white text-black px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-gray-100 transition-colors"><svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg><div class="text-left"><p class="text-[10px]">Download on the</p><p class="font-semibold text-sm">App Store</p></div></div>';
                    }}
                  />
                </a>
                <a href="#" className="block">
                  <img 
                    src="/google-play-badge.png"
                    alt="Get it on Google Play"
                    className="h-11 hover:opacity-80 transition-opacity"
                    onError={(e) => {
                      e.target.outerHTML = '<div class="bg-white text-black px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-gray-100 transition-colors"><svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg><div class="text-left"><p class="text-[10px]">GET IT ON</p><p class="font-semibold text-sm">Google Play</p></div></div>';
                    }}
                  />
                </a>
              </motion.div>
            </motion.div>

            {/* Right Side - Interactive Calculator */}
            <div className="hidden lg:flex items-center justify-center">
              <HeroCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* Partners / Featured In Section */}
      <section className="relative overflow-hidden bg-black py-12">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Title Badge with Green Glow */}
            <div className="flex justify-center mb-10">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-full">
                <span className="text-sm tracking-wide text-[#4ADE80] font-semibold uppercase">Trusted by Industry Leaders</span>
              </div>
            </div>
            
            {/* Partner Logos Grid - Grayscale */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
              {[
                { name: 'HDFC Bank', url: 'https://upload.wikimedia.org/wikipedia/commons/2/28/HDFC_Bank_Logo.svg' },
                { name: 'ICICI Bank', url: 'https://upload.wikimedia.org/wikipedia/commons/1/12/ICICI_Bank_Logo.svg' },
                { name: 'Knight Frank', url: 'https://logos-world.net/wp-content/uploads/2023/01/Knight-Frank-Logo.png' },
                { name: 'Colliers', url: 'https://companieslogo.com/img/orig/CIGI-68e535d9.png' },
                { name: 'JLL', url: 'https://logos-world.net/wp-content/uploads/2021/02/JLL-Logo.png' },
                { name: 'CBRE', url: 'https://logos-world.net/wp-content/uploads/2022/04/CBRE-Symbol.png' }
              ].map((partner, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex items-center justify-center grayscale hover:grayscale-0 transition-all opacity-50 hover:opacity-100"
                >
                  <img 
                    src={partner.url}
                    alt={partner.name}
                    className="h-8 w-auto object-contain"
                    onError={(e) => {
                      e.target.outerHTML = `<p class="text-white/40 text-sm font-medium">${partner.name}</p>`;
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section - Black Background */}
      <section className="relative bg-black py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
            {/* Left Side - Heading */}
            <div>
              <p className="text-gray-500 text-sm font-light mb-4 tracking-wide uppercase">How it works</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-6 leading-tight">
                Own real assets, one fraction at a time.
              </h2>
              <p className="text-base text-gray-400 leading-relaxed max-w-lg">
                ALTX is a next-generation platform designed to simplify ownership of wealth-generating assets—such as real estate, income assets, and collectibles—through fractional ownership, all through your phone.
              </p>
            </div>

            {/* Right Side - Benefit Cards */}
            <div className="space-y-6">
              {/* Card 1: Browse */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#4ADE80]/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#4ADE80]/10 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#4ADE80]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Browse</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Access curated investment opportunities to pick one most suitable to your criteria.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card 2: Invest */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#4ADE80]/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#4ADE80]/10 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#4ADE80]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Invest</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Invest with confidence, with funds secured in a transparent, real-time escrow mechanism.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card 3: Earn */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#4ADE80]/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#4ADE80]/10 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#4ADE80]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Earn</h3>
                    <p className="text-sm text-gray-400 leading-relaxed mb-4">
                      Sit back with ease and rest assured knowing your portfolio is earning for you 24/7.
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-[#4ADE80] rounded flex items-center justify-center">
                        <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-300 font-medium">Paid directly to your wallet</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Sell */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#4ADE80]/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#4ADE80]/10 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#4ADE80]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Sell</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Realise your full investment appreciation at maturity or take early profits by selling within our community.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the page content */}
      <div className="relative z-10 bg-[#050505]">
        {/* Market Growth Charts */}
        <RealEstateMarketGrowth />
        <FractionalMarketGrowth />

        {/* Membership Benefits */}
        <MembershipBenefits />

        {/* FAQ Section */}
        <FAQSection />

        {/* Final CTA */}
        <section className="py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4ADE80]/10 via-transparent to-[#4ADE80]/5"></div>
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <h2 className="text-4xl sm:text-5xl lg:text-7xl font-light mb-8 leading-tight tracking-tight">
                NOT EVERYONE
                <span className="block mt-2 text-[#4ADE80]">GETS IT.</span>
              </h2>
              <p className="text-xl text-white/50 mb-12 leading-relaxed">
                like all good things in life, building wealth through fractional real estate 
                is not for everyone. but the possibility of financial freedom makes 
                the journey worthwhile.
              </p>
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-white text-black hover:bg-white/90 font-medium px-12 py-7 text-lg rounded-full"
              >
                JOIN THE CLUB
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/5 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <p className="text-2xl font-light tracking-tight mb-2">ALTX</p>
                <p className="text-white/40 text-sm mb-2">© 2025 Altx. All rights reserved.</p>
                <p className="text-white/30 text-xs">
                  Investing involves risk. Past performance is not indicative of future results.
                </p>
              </div>
              <div className="flex items-center space-x-8 text-sm text-white/40">
                <a href="#" className="hover:text-white transition-colors">Terms</a>
                <a href="#" className="hover:text-white transition-colors">Privacy</a>
                <a href="#" className="hover:text-white transition-colors">Risk Disclosure</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
      
    </div>
  );
}
